// Unpublished Work © 2026 Deere & Company.

import {datadogRum} from '@datadog/browser-rum';
import {datadogLogs} from '@datadog/browser-logs';
import {datadogLogger} from '@deere/datadog-action-logger';

const rumEnvironments = ['qual', 'cert', 'prod'];
const APP_LOAD_ACTION = 'app-load';
const DATA_LOAD_ACTION = 'data-load';
const LOAD_STEP = 'load';
// Public Datadog browser RUM client token (pub-prefixed). Intended to be embedded in browser code
// so the SDK can post telemetry from end-user browsers. Not a secret; cannot read data from Datadog.
const DATADOG_CLIENT_TOKEN = 'pubc49485a5741d82dca985047f5d8c7796';

let isSessionReplayEnabled = false,
    isRumInitialized = false,
    hasLoadTrackingStarted = false,
    hasAppLoadCompleted = false,
    hasDataLoadCompleted = false;

function shouldAttachMetadata(metadata) {
    return metadata && Object.keys(metadata).length > 0;
}

function shouldInitializeRum(env) {
    return rumEnvironments.includes(env);
}

export function initRum({
    env, ddVersion
}) {
    if (shouldInitializeRum(env)) {
        datadogRum.init({
            applicationId: '27c4da40-3c3d-4784-b7b4-369da7e9ec95',
            clientToken: DATADOG_CLIENT_TOKEN,
            site: 'datadoghq.com',
            service: 'onlink',
            env,
            version: ddVersion,
            sessionSampleRate: 100,
            trackUserInteractions: true,
            trackResources: true,
            trackLongTasks: true,
            trackViewsManually: false,
            defaultPrivacyLevel: 'mask-user-input',
            allowedTracingUrls: [
                /^https:\/\/[a-zA-Z0-9.-]*\.deere\.com([/?#:]|$)/,
                /^https:\/\/[a-zA-Z0-9.-]*\.johndeerecloud\.com([/?#:]|$)/
            ],
            startSessionReplayRecordingManually: true,
            sessionReplaySampleRate: 100
        });

        datadogLogs.init({
            clientToken: DATADOG_CLIENT_TOKEN,
            site: 'datadoghq.com',
            service: 'onlink',
            env,
            version: ddVersion,
            forwardErrorsToLogs: true,
            forwardConsoleLogs: 'all',
            sessionSampleRate: 100
        });

        datadogLogger.setRumInstance(datadogRum);
        datadogRum.setGlobalContextProperty('jd_component', 'onlink');
        isRumInitialized = true;
    } else {
        isRumInitialized = false;
    }

    hasLoadTrackingStarted = false;
    hasAppLoadCompleted = false;
    hasDataLoadCompleted = false;
}

export function startLoadTracking(metadata = {}) {
    if (!isRumInitialized || hasLoadTrackingStarted) {
        return;
    }

    datadogLogger.startTracking(APP_LOAD_ACTION, [LOAD_STEP]);
    datadogLogger.recordActionStepStart(APP_LOAD_ACTION, LOAD_STEP);
    datadogLogger.startTracking(DATA_LOAD_ACTION, [LOAD_STEP]);
    datadogLogger.recordActionStepStart(DATA_LOAD_ACTION, LOAD_STEP);

    if (shouldAttachMetadata(metadata)) {
        datadogLogger.recordActionMetadata(APP_LOAD_ACTION, metadata);
        datadogLogger.recordActionMetadata(DATA_LOAD_ACTION, metadata);
    }

    hasLoadTrackingStarted = true;
}

export function completeAppLoad(metadata = {}) {
    if (!isRumInitialized || !hasLoadTrackingStarted || hasAppLoadCompleted) {
        return;
    }

    datadogLogger.recordActionStepCompleted(APP_LOAD_ACTION, LOAD_STEP, metadata);
    hasAppLoadCompleted = true;
}

export function completeDataLoad(metadata = {}) {
    if (!isRumInitialized || !hasLoadTrackingStarted || hasDataLoadCompleted) {
        return;
    }

    datadogLogger.recordActionStepCompleted(DATA_LOAD_ACTION, LOAD_STEP, metadata);
    hasDataLoadCompleted = true;
}

export function enableSessionReplay() {
    if (!isSessionReplayEnabled) {
        datadogRum.startSessionReplayRecording();
        isSessionReplayEnabled = true;
    }
}

export function setRumAccount({
    orgId, name
} = {}) {
    if (!isRumInitialized || !orgId) {
        return;
    }

    datadogLogs.setAccount({
        id: String(orgId),
        name
    });
}

export function setRumUser({
    appUserId, membershipId, myJdId
} = {}) {
    if (!isRumInitialized || !appUserId) {
        return;
    }

    datadogLogs.setUser({
        id: String(appUserId),
        membershipId,
        myJdId
    });
}

export function resetRumTrackingForTests() {
    isSessionReplayEnabled = false;
    isRumInitialized = false;
    hasLoadTrackingStarted = false;
    hasAppLoadCompleted = false;
    hasDataLoadCompleted = false;
}
